import { useMemo } from "react";

import type { Insights, LanguageCopy, LanguageKey } from "../api/types";
import { buildComparisonInsights } from "../insights";
import { rankedByScore } from "../utils/ranking";

import { BarChart } from "./BarChart";
import { InsightCard } from "./InsightCard";
import { LanguageCardCarousel } from "./LanguageCardCarousel";

type Props = {
  youScores: Record<LanguageKey, number>;
  /** Present only when the partner has submitted. */
  partnerScores?: Record<LanguageKey, number>;
  languages: Record<LanguageKey, LanguageCopy>;
  languageOrder: LanguageKey[];
  partnerName: string;
  /** Present only when both have submitted. Drives the two insight cards. */
  insights?: Insights;
};

export function AssessmentResults({
  youScores,
  partnerScores,
  languages,
  languageOrder,
  partnerName,
  insights,
}: Props) {
  // Stable identity so LanguageCardCarousel's scroll listener isn't re-attached
  // every parent render.
  const ranked = useMemo(
    () => rankedByScore(languageOrder, youScores),
    [languageOrder, youScores],
  );
  const primary = ranked[0];
  const bothDone = Boolean(partnerScores && insights);

  const comparisonCards = useMemo(() => {
    if (!partnerScores || !insights) return null;
    return buildComparisonInsights({
      languageOrder,
      languages,
      youScores,
      partnerScores,
      partnerName: insights.partner_name,
    });
  }, [languageOrder, languages, youScores, partnerScores, insights]);

  return (
    <div className="mx-auto max-w-md px-6 pt-12 pb-16">
      <p className="wren-eyebrow mb-4 text-center text-[0.875rem]">
        {bothDone ? "Together" : "Your results"}
      </p>
      <h1 className="mb-8 text-center text-[2.5rem] leading-[100%]">
        {bothDone ? "How you both receive love" : "How you receive love"}
      </h1>

      {/* Hero chart card — always renders the both-partners shape so the
          waiting and both-done states feel like the same picture, just with
          the partner bars filling in when they submit. */}
      <div
        className="mb-8 border border-[color:var(--color-wren-border)] bg-white p-8"
        style={{
          borderRadius: "var(--radius-wren-card)",
          boxShadow: "var(--shadow-wren-button)",
        }}
      >
        <BarChart
          languageOrder={languageOrder}
          languages={languages}
          youScores={youScores}
          partnerScores={partnerScores}
          partnerName={partnerName}
          partnerPending={!partnerScores}
        />
        <div className="mt-6 flex items-center justify-center gap-6 text-[0.75rem] text-[color:var(--color-wren-gray-700)]">
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5"
              style={{ backgroundColor: "var(--color-wren-green)", borderRadius: 2 }}
            />
            You
          </span>
          <span
            className={`inline-flex items-center gap-2 ${partnerScores ? "" : "opacity-60"}`}
          >
            <span
              className="inline-block h-2.5 w-2.5"
              style={{
                backgroundColor: partnerScores
                  ? "var(--color-wren-light-brown)"
                  : "transparent",
                border: partnerScores
                  ? "none"
                  : "1px dashed var(--color-wren-light-brown)",
                borderRadius: 2,
              }}
            />
            {partnerName}
          </span>
        </div>
        <blockquote className="mt-6 border-t border-[color:var(--color-wren-light-primary)] pt-6 text-center font-serif text-[1.25rem] italic leading-[1.4] text-[color:var(--color-wren-green-dark)]">
          &ldquo;{languages[primary].top_intro}&rdquo;
        </blockquote>
      </div>

      <LanguageCardCarousel
        ranked={ranked}
        languages={languages}
        youScores={youScores}
      />

      {bothDone && comparisonCards ? (
        <div className="mt-12 space-y-6">
          <InsightCard
            title={comparisonCards.card1.title}
            body={comparisonCards.card1.body}
          />
          <InsightCard
            title={comparisonCards.card2.title}
            body={comparisonCards.card2.body}
            tryThis={comparisonCards.card2.tryThis}
          />
        </div>
      ) : (
        <p
          role="status"
          className="mt-12 text-center text-[0.9375rem] italic leading-[1.5rem] text-[color:var(--color-wren-light-brown)]"
        >
          {`${partnerName} hasn't finished theirs yet. I'll ping you both when they do, and you'll see the side-by-side.`}
        </p>
      )}
    </div>
  );
}
